import Drawer from '@/components/Drawer';
import { FileContext, FileKind, FileModel } from '@/typings/api/files';
import { F } from '@/utils/intl';
import { Button, Pagination, Space } from 'antd';
import React, { useEffect, useState } from 'react';
import FileGrid from './FileGrid';
import FileLibraryToolbar from './FileLibraryToolbar';
import { useFileLibrary } from './useFileLibrary';

import styles from './FilePickerDrawer.less';

interface FilePickerDrawerProps {
  open: boolean;
  onClose: () => void;
  onSelect: (file: FileModel) => void;
  /** Narrows the library to what this slot can use. */
  kind?: FileKind;
  /** What the upload is for, recorded on the file. */
  context?: FileContext;
  title?: React.ReactNode;
}

/**
 * Choose a file, or upload a new one.
 *
 * Both halves matter: an image already in the library should be reused rather
 * than uploaded again under a second name, and a picker that could only browse
 * would force a detour through the file manager to add anything.
 *
 * Uploading here selects immediately — asking someone to upload a file and then
 * find and click it is a step with no decision in it.
 *
 * Mounted by whoever opens it (docs/drawers.md: editor-local drawers are not
 * registered in `DvaWatcher`), so it stacks above the drawer it was raised from
 * and that drawer keeps its state.
 */
const FilePickerDrawer: React.FC<FilePickerDrawerProps> = ({
  open,
  onClose,
  onSelect,
  kind,
  context,
  title,
}) => {
  const library = useFileLibrary({ kind });
  const [selected, setSelected] = useState<FileModel | null>(null);

  useEffect(() => {
    if (open) {
      setSelected(null);
      // Back to an unfiltered first page, and reloaded even when that is what
      // was already showing: something may have been uploaded elsewhere since.
      library.applyFilters({ kind, search: '' });
      library.refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const choose = (file: FileModel) => {
    onSelect(file);
    onClose();
  };

  return (
    <Drawer
      open={open}
      onOpenChange={(next) => !next && onClose()}
      width="min(900px, calc(100vw - 120px))"
      title={
        title ?? (
          <F
            id="components.fileLibrary.picker.title"
            defaultMessage="Choose a file"
          />
        )
      }
    >
      <div className={styles.picker}>
        <FileLibraryToolbar
          search={library.filters.search}
          onSearch={(search) => library.applyFilters({ search })}
          kind={library.filters.kind}
          onKindChange={
            kind ? undefined : (next) => library.applyFilters({ kind: next })
          }
          kinds={kind ? [kind] : undefined}
          uploading={library.uploading}
          onUpload={async (file) => {
            const uploaded = await library.upload(file, { context });
            if (uploaded) choose(uploaded);
          }}
        />

        <div className={styles.body}>
          <FileGrid
            files={library.items}
            loading={library.loading}
            selectedId={selected?.id}
            // Clicking marks the file; "Use file" commits it. Committing on the
            // first click would turn a mis-click into a silent edit of whatever
            // the picker was opened from.
            onSelect={setSelected}
          />
        </div>

        <div className={styles.footer}>
          {library.total > library.pageSize ? (
            <Pagination
              current={library.page}
              pageSize={library.pageSize}
              total={library.total}
              showSizeChanger={false}
              onChange={library.setPage}
            />
          ) : (
            <span />
          )}

          <Space>
            <Button onClick={onClose}>
              <F id="common.actions.cancel" defaultMessage="Cancel" />
            </Button>
            <Button
              type="primary"
              disabled={!selected}
              onClick={() => selected && choose(selected)}
            >
              <F
                id="components.fileLibrary.picker.select"
                defaultMessage="Use file"
              />
            </Button>
          </Space>
        </div>
      </div>
    </Drawer>
  );
};

export default FilePickerDrawer;
